import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import isHotkey from 'is-hotkey'
import ReactDOM from 'react-dom'

import {
  Editor,
  Transforms,
  createEditor,
  Range,
  Descendant,
  Element as SlateElement,
} from 'slate';
import {
  Slate,
  Editable,
  ReactEditor,
  withReact,
  useSlate,
  useSelected,
  useFocused,
} from 'slate-react';
import { withHistory } from 'slate-history';
import {IconButton, Link, SvgIcon} from "@material-ui/core";
import IconRenderer from "../IconRenderer";
import {Box, Typography} from "@material-ui/core/index";
import TextField from "../TextField";
import {makeStyles} from "@material-ui/core/styles";
import clsx from "clsx";
import useGetRequest from "../../hooks/useGetRequest";

export const VARIABLE_TYPE = 'variable';

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
}

export type CustomText = {
  bold?: boolean,
  italic?: boolean,
  code?: boolean,
  text: string,
}

export type FieldElement = {
  type: 'variable',
  variable: string,
  children: CustomText[],
  slug: string,
}

const useStyles = makeStyles((theme) => ({
  markButton: {
    borderRadius: 4,
    padding: 4,
    marginRight: 4,
  },
  icon: {
    height: theme.spacing(1),
    width: theme.spacing(1),
  },
  variablesAutocomplete: {
    padding: 4,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: '2px solid #ccc',
    boxShadow: theme.shadows[10],
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.secondary.contrastText,
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.h6.fontWeight,
    display: 'flex',
    flexDirection: 'column',
  },
  variable: {
    padding: '2px 4px',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    }
  },
  selectedVariable: {
    color: theme.palette.primary.main,
  },
  field: {
    padding: '3px 3px 2px',
    margin: '1px 1px',
    verticalAlign: 'baseline',
    display: 'inline-block',
    borderRadius: 4,
    backgroundColor: theme.palette.secondary.contrastText,
    color: theme.palette.background.default,
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.h6.fontWeight,
  },
  selectedField: {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
  }
}));


const withFields = (editor: any) => {
  const { isInline, isVoid } = editor

  editor.isInline = (element: any) => {
    return element.type === VARIABLE_TYPE ? true : isInline(element)
  }

  editor.isVoid = (element: any) => {
    return element.type === VARIABLE_TYPE ? true : isVoid(element)
  }

  return editor
}

const insertField = (editor: any, variable: any) => {
  const variableElement: FieldElement = {
    type: VARIABLE_TYPE,
    variable: variable.label,
    children: [{ text: '' }],
    slug: variable.value
  }
  Transforms.insertNodes(editor, variableElement)
  Transforms.move(editor)
}

export const Portal = ({ children }: any) => {
  return typeof document === 'object'
    ? ReactDOM.createPortal(children, document.body)
    : null
}

const Field = ({ attributes, children, element }: any) => {
  const selected = useSelected()
  const focused = useFocused()
  const classes = useStyles();
  return (
    <span
      {...attributes}
      contentEditable={false}
      className={clsx({
        [classes.field]: true,
        [classes.selectedField]: selected && focused,
      })}
    >
      {element.variable}
      {children}
    </span>
  )
}

const LIST_TYPES = ['numbered-list', 'bulleted-list']

function InputComponent(props: any) {
  const { component: Component, inputRef, ...other } = props;

  // implement `InputElement` interface
  React.useImperativeHandle(inputRef, () => ({
    focus: () => {
      // logic to focus the rendered component from 3rd party belongs here
    },
    // hiding the value e.g. react-stripe-elements
  }));

  // `Component` will be your `SomeThirdPartyComponent` from below
  return <Component {...other} />;
}

const SlateRichTextArea = (props: any) => {
  const value = props.value ? JSON.parse(props.value) : null; // weird - if this happens inside the RichTextArea, variables no longer work?
  const { editorClassName, onChange, showToolbar, readOnly, workflowId, disableVariables, ...rest } = props;
  const richTextAreaProps = { editorClassName, onChange, showToolbar, readOnly, workflowId, disableVariables };

  if (readOnly || editorClassName) return <RichTextArea {...richTextAreaProps} value={value} />

  return (
    <TextField
      {...props}
      InputProps={{
        inputComponent: InputComponent,
        inputProps: {
          component: RichTextArea,
          ...richTextAreaProps,
          value,
        },
      }}
    />
  )
};

const defaultValue = [
  {
    // @ts-ignore
    type: 'paragraph',
    children: [
      { text: '' },
    ],
  },
];

const RichTextArea = ({ editorClassName, value, onChange, showToolbar, readOnly, workflowId, disableVariables}: any) => {
  // // todo - this goes into a different editor component
  const { result: variables, } = useGetRequest({
      url: `/automatic/workflows/${workflowId}/variables/`,
      initialResult: [],
      shouldFetchOnInitialRender: Boolean(!disableVariables && workflowId)
  });

  const renderElement = useCallback(props => <Element {...props} />, [])
  const renderLeaf = useCallback(props => <Leaf {...props} />, [])
   const editor = useMemo(
    // @ts-ignore
    () => withFields(withReact(withHistory(createEditor()))),
    []
  );

  const classes = useStyles();

  const ref = useRef<HTMLDivElement | null>()
  const [target, setTarget] = useState<Range | undefined>()
  const [index, setIndex] = useState(0)
  const [search, setSearch] = useState('')

  const chars = variables.filter(({ label }: { label: string }) =>
    label.toLowerCase().startsWith(search.toLowerCase())
  )

  const onKeyDown = useCallback(
    event => {
      for (const hotkey in HOTKEYS) {
          if (isHotkey(hotkey, event as any)) {
            event.preventDefault()
            // @ts-ignore
            const mark = HOTKEYS[hotkey]
            toggleMark(editor, mark)
          }
        }

      if (target) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault()
            const prevIndex = index >= chars.length - 1 ? 0 : index + 1
            setIndex(prevIndex)
            break
          case 'ArrowUp':
            event.preventDefault()
            const nextIndex = index <= 0 ? chars.length - 1 : index - 1
            setIndex(nextIndex)
            break
          case 'Tab':
          case 'Enter':
            event.preventDefault()
            // @ts-ignore
            Transforms.select(editor, target)
            insertField(editor, chars[index])
            // @ts-ignore
            setTarget(null)
            break
          case 'Escape':
            event.preventDefault()
            // @ts-ignore
            setTarget(null)
            break
        }
      }
    },
    [index, search, target]
  )

  useEffect(() => {
    if (ref.current && target && chars.length > 0) {
      const el = ref.current
      const domRange = ReactEditor.toDOMRange(editor, target)
      const rect = domRange.getBoundingClientRect()
      el.style.top = `${rect.top + window.pageYOffset + 24}px`
      el.style.left = `${rect.left + window.pageXOffset}px`
    }
  }, [chars.length, editor, index, search, target])

  return (
    <Box style={{ padding: readOnly ? 0 : '18.5px 14px 8px 14px', width: '100%' }}>
      <Typography>
        <Slate editor={editor} value={value || defaultValue} onChange={val => {
          onChange(JSON.stringify(val));
          const { selection } = editor

          if (selection && Range.isCollapsed(selection)) {
            // @ts-ignore
            const [start] = Range.edges(selection)
            const wordBefore = Editor.before(editor, start, { unit: 'word' })
            const before = wordBefore && Editor.before(editor, wordBefore)
            const beforeRange = before && Editor.range(editor, before, start)
            const beforeText = beforeRange && Editor.string(editor, beforeRange)
            const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/)
            const after = Editor.after(editor, start)
            const afterRange = Editor.range(editor, start, after)
            const afterText = Editor.string(editor, afterRange)
            const afterMatch = afterText.match(/^(\s|$)/)

            if (beforeMatch && afterMatch) {
              // @ts-ignore
              setTarget(beforeRange)
              setSearch(beforeMatch[1])
              setIndex(0)
              return
            }
          }

          // @ts-ignore
          setTarget(null)
        }}>
          <Editable
            onKeyDown={onKeyDown}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder="Enter some rich text…"
            spellCheck
            autoFocus
            readOnly={readOnly}
            className={editorClassName}
          />
          {target && chars.length > 0 && (
            <Portal>
              <span
                // @ts-ignore
                ref={ref}
                className={classes.variablesAutocomplete}
                style={{
                  top: '-9999px',
                  left: '-9999px',
                  position: 'absolute',
                  zIndex: 2000,
                }}
              >
                {chars.map((char: any, i: number) => (
                  <span
                    key={char.label}
                    className={clsx({
                      [classes.variable]: true,
                      [classes.selectedVariable]: i === index
                    })}
                  >
                    {char.label}
                  </span>
                ))}
              </span>
            </Portal>
          )}
          {
            showToolbar ? (
              <span>
                <MarkButton format="bold" icon="bold" />
                <MarkButton format="italic" icon="italic" />
                <MarkButton format="underline" icon="underline" />
                <BlockButton format="numbered-list" icon="list" />
                <BlockButton format="bulleted-list" icon="list" />
              </span>
            ) : <span style={{ paddingBottom: 10.5 }} />
          }
        </Slate>
      </Typography>
    </Box>
  )
}

const toggleBlock = (editor: any, format: any) => {
  const isActive = isBlockActive(editor, format)
  const isList = LIST_TYPES.includes(format)

  Transforms.unwrapNodes(editor, {
    match: n =>
      LIST_TYPES.includes(
        // @ts-ignore
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type
      ),
    split: true,
  })
  const newProperties: Partial<SlateElement> = {
    // @ts-ignore
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  }
  Transforms.setNodes(editor, newProperties)

  if (!isActive && isList) {
    const block = { type: format, children: [] }
    Transforms.wrapNodes(editor, block)
  }
}

const toggleMark = (editor: any, format: any) => {
  const isActive = isMarkActive(editor, format)

  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

const isBlockActive = (editor: any, format: any) => {
  // @ts-ignore
  const [match] = Editor.nodes(editor, {
    match: n =>
      // @ts-ignore
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
  })

  return !!match
}

const isMarkActive = (editor: any, format: any) => {
  const marks = Editor.marks(editor)
  // @ts-ignore
  return marks ? marks[format] === true : false
}

const Element = ({ attributes, children, element }: any) => {
  switch (element.type) {
    case 'link':
      return (
        <Link {...attributes} href={element.url}>
          {children}
        </Link>
      )
    case VARIABLE_TYPE:
      return <Field attributes={attributes} element={element}>{children}</Field>
    case 'block-quote':
      return <blockquote {...attributes}>{children}</blockquote>
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>
    case 'heading-one':
      return <h1 {...attributes}>{children}</h1>
    case 'heading-two':
      return <h2 {...attributes}>{children}</h2>
    case 'list-item':
      return <li {...attributes}>{children}</li>
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>
    default:
      return <span {...attributes}>{children}</span>
  }
}

const Leaf = ({ attributes, children, leaf }: any) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }

  if (leaf.code) {
    children = <code>{children}</code>
  }

  if (leaf.italic) {
    children = <em>{children}</em>
  }

  if (leaf.underline) {
    children = <u>{children}</u>
  }

  return <span {...attributes}>{children}</span>
}

const BlockButton = ({ format, icon }: any) => {
  const editor = useSlate();
  const classes = useStyles();
  return (
    <IconButton
      className={classes.markButton}
      // active={isBlockActive(editor, format)}
      onClick={event => {
        event.preventDefault()
        toggleBlock(editor, format)
      }}
    >
      <SvgIcon fontSize="small">
      <IconRenderer iconName={icon} />
      </SvgIcon>
    </IconButton>
  )
}

const MarkButton = ({ format, icon }: any) => {
  const editor = useSlate()
  const classes = useStyles();
  return (
    <IconButton
      className={classes.markButton}
      // active={isMarkActive(editor, format)}
      onClick={event => {
        event.preventDefault()
        toggleMark(editor, format)
      }}
    >
      <SvgIcon fontSize="small">
      <IconRenderer iconName={icon} />
      </SvgIcon>
    </IconButton>
  )
}

export default SlateRichTextArea;
